import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import caseEvidenceService from "@/features/case-evaluation/api/services/evidence-service";
import type { CaseActivity } from "@/features/case-evaluation/types/activities";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import caseListsService from "@/features/case-record/api/services/case-records-service";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { useEventsMutation } from "@/features/dashboard/api/queries/event-queries.ts";
import eventService from "@/features/dashboard/api/services/event-service";
import { IconAlertCircle, IconCirclePlus, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { add, debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import Alert from "../core/alert";
import { Button } from "../core/buttons/button";
import Combobox from "../core/forms/combobox";
import TagsInput from "../core/forms/tags-input";
import { useModalControl } from "../core/modals/use-modal-controller";
import Tooltip from "../core/tooltip";

interface EventsRow {
	title: string;
	assigned_to: string;
	remarks: string;
	due_date: string;
	stage: string;
	hearing_mode: string;
	exhibits: string;
	hearing_count: number;
	calendar_id: string;
	cases: string[];
	// end_at: string;
}

export default function CaseEventsModal({
	caseClassification,
	calendarId,
	data,
	branchId,
	// caseData,
	// stage,
	// handleSubmit,
}: {
	data: CaseActivity;
	// caseData: CaseRecord;
	caseClassification?: string;
	calendarId?: string;
	branchId?: string;
	// stage?: string;
	handleSubmit: (values: {
		events: EventsRow[];
	}) => void;
}) {
	const addEventsMutation = useEventsMutation();
	const handleSubmit = async (data: EventsRow) => {
		await addEventsMutation.mutateAsync({
			data,
			branchId,
		});
		closeModal();
	};

	const caseStageQuery = useSuspenseQuery(
		caseStagesQueryOptions({
			case_classification: caseClassification ?? "",
			status: "",
		}),
	);
	const { closeModal } = useModalControl();
	const form = useForm({
		defaultValues: {
			events: [
				{
					title: "",
					type: "Hearing",
					assigned_to: "Both",
					remarks: "",
					due_date: "",
					// stage: data?.stage || stage,
					hearing_mode: "",
					exhibits: "",
					calendar_id: calendarId || "",
					end_at: "",
				},
			],
		},
	});
	const events = useFieldArray({
		control: form.control,
		name: "events",
	});

	const [debouncedDate, setDebouncedDate] = useState("");
	const [parties, setParties] = useState<string>();
	// const [isHoliday, setIsHoliday] = useState(false);
	const [selectedCase, setSelectedCase] = useState<{ id: string; case_number: string } | null>(null);
	const selectedCaseId = form.watch("cases");
	const [rowCounts, setRowCount] = useState(0);

	const handleAddRow = () => {
		events.append({
			title: "",
			type: "Hearing",
			assigned_to: "Both",
			remarks: "",
			due_date: "",
			// stage: stage || "",
			hearing_mode: "",
			exhibits: "",
			calendar_id: calendarId || "",
			end_at: "",
		});
	};

	const hearingDate = form.watch("events").map((hearing) => {
		return hearing.due_date;
	});

	const hearingRemarks = form.watch("events").map((hearing) => {
		return hearing.remarks;
	});

	const updateDebouncedDate = useCallback(
		debounce(
			(newDate) => {
				setDebouncedDate(newDate);
			},
			500,
			{ trailing: true },
		),
		[],
	);

	useEffect(() => {
		if (hearingDate) {
			const dates = Array.isArray(hearingDate) ? hearingDate : [hearingDate];

			let hasEmptyDate = false;

			dates.map((data) => {
				if (!data || data.trim() === "") {
					hasEmptyDate = true;
				} else {
					const parsedDate = new Date(data);
					if (!Number.isNaN(parsedDate.getTime())) {
						updateDebouncedDate(format(parsedDate, "yyyy-MM-dd"));
					}
				}
			});
			if (hasEmptyDate) {
				setRowCount(events.fields.length - 1);
			} else {
				setRowCount(events.fields.length);
			}
		}
	}, [hearingDate, updateDebouncedDate]);

	// useEffect(() => {
	//     if (debouncedDate) {
	//         eventService
	//             .validateHolidays(debouncedDate)
	//             .then((response) => {
	//                 if (response.id) {
	//                     setIsHoliday(true);
	//                 } else {
	//                     setIsHoliday(false);
	//                 }
	//             })
	//             .catch((_) => {
	//                 setIsHoliday(false);
	//             });
	//     }
	// }, [debouncedDate, hearingDate]);

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-4">
					{/* {isHoliday && (
                        <div className="col-span-12 w-full py-1">
                            <Alert type="info" allowClose={false} title="The date selected is a Holiday" icon={<IconAlertCircle size={20} stroke={2} />}>
                                The selected date falls on a holiday. Please take this into consideration when scheduling this hearing. You can still schedule a hearing on this
                                date.
                            </Alert>
                        </div>
                    )} */}

					<div className="flex flex-col gap-4">
						{events.fields.map((hearing, index) => (
							<div className="border rounded-md w-full p-4" key={index}>
								<div className="flex flex-col">
									<div className="flex flex-row">
										<div className="flex flex-col w-full gap-3">
											<div className="flex flex-row items-center justify-between">
												<div className="flex flex-col w-full">
													<span className="text-lg font-semibold text-foreground-dark">Event # {index + 1}</span>
												</div>
												<button
													className="inline-flex justify-center items-center px-3 text-danger-600 hover:text-danger-500"
													type="button"
													onClick={() => events.remove(index)}
												>
													<IconTrash size={18} stroke={2} />
												</button>
											</div>
											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
												<div className="col-span-3 w-full">
													<FormField
														control={form.control}
														name={`events[${index}].due_date`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Date From</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<Input
																		type="datetime-local"
																		onChange={async (e) => {
																			const value = e.target.value;
																			const calendarId = form.watch(`events.${index}.calendar_id`);
																			// const response = await eventService.validateHearing(value, caseData.id);
																			const response = await eventService.validateHearingDateByCalendarId(value, calendarId);
																			form.setValue(`events.${index}.hearing_count`, response);
																			field.onChange(value);
																		}}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
												<div className="col-span-3 w-full">
													<FormField
														control={form.control}
														name={`events[${index}].end_at`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Date To</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<Input
																		type="datetime-local"
																		onChange={async (e) => {
																			const value = e.target.value;
																			const calendarId = form.watch(`events.${index}.calendar_id`);
																			// const response = await eventService.validateHearing(value, caseData.id);
																			const response = await eventService.validateHearingDateByCalendarId(value, calendarId);
																			form.setValue(`events.${index}.hearing_count`, response);
																			field.onChange(value);
																		}}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
												<div className="col-span-5 w-full">
													<FormField
														control={form.control}
														name={`events[${index}].title`}
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Tittle</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup>
																	<Input autoComplete="off" placeholder="e.g Event Title" {...field} />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
												<div className="col-span-1 w-full">
													<FormField
														control={form.control}
														name={`events.${index}.hearing_count`}
														render={({ field }) => {
															return (
																<FormItem>
																	<div className="flex flex-col w-full">
																		<FormLabel>Settings</FormLabel>
																		<FormDescription />
																	</div>
																	<FormGroup>
																		<Tooltip className="z-[999]" content="Click to open calendar in new tab" side="top">
																			<Button
																				className="w-full !justify-start !containerbg-muted text-muted-foreground"
																				variant="ghost"
																				onClick={() => {
																					window.open(`/calendar?view=true&date=${form.getValues(`events.${index}.due_date`)}`);
																				}}
																			>
																				{field.value || 0}
																			</Button>
																			{/* </button> */}
																		</Tooltip>
																	</FormGroup>
																</FormItem>
															);
														}}
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
					<Button type="button" onClick={handleAddRow} size="default" variant="ghost">
						<IconCirclePlus size={18} stroke={2} /> Add New Schedule
					</Button>
					<div className="flex flex-row justify-between w-full">
						<Button
							className=""
							variant="ghost"
							type="button"
							onClick={() => {
								closeModal();
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={form.formState.isSubmitting} loading={form.formState.isSubmitting} variant="default">
							Confirm
						</Button>
					</div>
				</div>
			</form>
		</Form>
	);
}
